import React, { useState, useEffect, useCallback } from 'react';
import { DatePicker, Input, Tag } from 'antd';
import { CheckCircleOutlined } from '@ant-design/icons';
import moment from 'moment';
import locale from 'antd/es/date-picker/locale/vi_VN';
import { useDispatch, useSelector } from 'react-redux';
import Cookies from 'universal-cookie';
import BKModal from 'components/BKModal';
import BKButton from 'components/BKButton';
import BKInput from 'components/BKInput';
import BKSelect from 'components/BKSelect';
import callApi from 'helpers/apiCaller';
import config from 'common/config';
import { getAuthHeader } from 'helpers/authHelper';
import { selectUser, checkAuth } from 'components/AuthStore';
import FaceLoginModal from './FaceLoginModal';
import LoginHistoryModal from './LoginHistoryModal';
import { SuccessModal } from './FaceRegisterPrompt';
import { faceRegister } from './services';
import './ModalUserProfile.css';

const cookies = new Cookies();

const GENDER_OPTIONS = [
  { value: 'M', label: 'Nam' },
  { value: 'F', label: 'Nữ' }
];

const DEFAULT_FORM = {
  fullname: '',
  gender: '',
  id_num: '',            // CCCD
  date_inssue_id_num: null,
  description: '',       // bio/giới thiệu
  email: '',
  cell_phone: '',
  address: '',
  facebook: '',
  instagram: '',
  tiktok: '',
  // required by API
  province_id: 0,
  district_id: 0,
  schools_id: []
};

const ModalUserProfile = ({ open, onClose }) => {
  const dispatch = useDispatch();
  const authUser = useSelector(selectUser);
  const [userData, setUserData] = useState(null);
  const [formValues, setFormValues] = useState(DEFAULT_FORM);
  const [provinces, setProvinces] = useState([]);
  const [districts, setDistricts] = useState([]);
  const [openFaceRegister, setOpenFaceRegister] = useState(false);
  const [faceLoading, setFaceLoading] = useState(false);
  const [faceError, setFaceError] = useState('');
  const [openFaceSuccess, setOpenFaceSuccess] = useState(false);
  const [openLoginHistory, setOpenLoginHistory] = useState(false);

  const header = getAuthHeader();

  useEffect(() => {
    if (open) {
      fetchAll();
    }
  }, [open]);

  const fetchAll = async () => {
    const [provinceRes, userRes] = await Promise.all([
      callApi(config.getRootURL() + '/v2/provinces', 'POST', {}, header),
      callApi(config.getRootURL() + '/gd/v3/user_infos', 'GET', {}, header)
    ]);

    if (provinceRes?.data?.data?.provices) {
      setProvinces(provinceRes.data.data.provices);
    }

    if (!userRes) return;
    const user = userRes.data?.data?.user;
    if (!user) return;

    setUserData(user);

    // Extract schools_id from schools array since schools_id field is null
    const schoolsId = Array.isArray(user.schools) && user.schools.length > 0
      ? user.schools.map((s) => s.school_id).filter(Boolean)
      : [];

    setFormValues({
      fullname: user.fullname || '',
      gender: user.gender || '',
      id_num: user.id_num || '',
      date_inssue_id_num: user.date_inssue_id_num
        ? moment(user.date_inssue_id_num).isValid()
          ? moment(user.date_inssue_id_num).format('YYYY-MM-DD')
          : null
        : null,
      description: user.description || '',
      email: user.email || '',
      cell_phone: user.cell_phone || '',
      address: user.address || '',
      facebook: user.facebook || '',
      instagram: user.instagram || '',
      tiktok: user.tiktok || '',
      province_id: user.province_id || 0,
      district_id: user.district_id || 0,
      schools_id: schoolsId
    });

    // Fetch districts if province is already set
    if (user.province_id) {
      fetchDistricts(user.province_id);
    }
  };

  const fetchDistricts = async (provinceId) => {
    if (!provinceId) return;
    const res = await callApi(
      config.getRootURL() + '/v2/districts',
      'POST',
      { province_id: parseInt(provinceId) },
      header
    );
    if (res?.data?.data?.districts) {
      setDistricts(res.data.data.districts);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormValues((prev) => ({ ...prev, [name]: value }));
  };

  const handleProvinceChange = (e) => {
    const provinceId = e.target.value;
    setFormValues((prev) => ({ ...prev, province_id: provinceId, district_id: 0 }));
    setDistricts([]);
    fetchDistricts(provinceId);
  };

  const handleDistrictChange = (e) => {
    setFormValues((prev) => ({ ...prev, district_id: e.target.value }));
  };

  const handleFaceCapture = useCallback(
    async (file) => {
      if (!file) return;
      setFaceLoading(true);
      setFaceError('');

      const res = await faceRegister(file, (err) => {
        const msg =
          err?.response?.data?.errors?.[0]?.message ||
          err?.response?.data?.message ||
          err?.message ||
          'Đăng ký khuôn mặt thất bại. Vui lòng thử lại.';
        setFaceError(msg);
      });

      setFaceLoading(false);

      if (res && res.status === 200) {
        const userStore = cookies.get('user_store');
        if (userStore) {
          const updatedUser = { ...userStore, face_login_registered: true };
          cookies.set(
            'user_store',
            JSON.stringify(updatedUser).replace(/</g, '\\u003c'),
            { path: '/' }
          );
        }
        if (authUser?.id) {
          localStorage.setItem('face_login_user_id', String(authUser.id));
          localStorage.setItem(
            'face_login_user_name',
            authUser.fullname || authUser.username || ''
          );
        }
        localStorage.removeItem('user_cache_timestamp');
        localStorage.removeItem('cached_user_data');
        localStorage.removeItem('cached_access_token');
        localStorage.removeItem('cached_data_hash');
        dispatch(checkAuth());
        setOpenFaceRegister(false);
        setOpenFaceSuccess(true);
      }
    },
    [dispatch, authUser]
  );

  const avatarSrc = userData?.avatar
    ? `${process.env.REACT_APP_DOMAIN_IMAGE}${userData.avatar}`
    : `${process.env.REACT_APP_STATIC_URL}content/assets/images/icon-user.png`;

  return (
    <>
    <BKModal
      open={open}
      onClose={onClose}
      title="THÔNG TIN CÁ NHÂN"
      width={780}
      style={{ maxWidth: '95vw' }}
      confirmClose={false}
      className="mup-modal"
      footer={
        <div className="mup-footer">
          <BKButton variant="secondary" onClick={onClose}>
            Đóng
          </BKButton>
          <BKButton variant="info" onClick={() => setOpenLoginHistory(true)}>
            Lịch sử đăng nhập
          </BKButton>
          <BKButton
            variant="success"
            onClick={() => {
              setFaceError('');
              setOpenFaceRegister(true);
            }}>
            {userData?.face_login_registered ? 'Cập nhật khuôn mặt' : 'Đăng ký khuôn mặt'}
          </BKButton>
        </div>
      }>
      <div className="mup-body">
        {/* Avatar + Bio */}
        <div className="mup-top-row">
          <div className="mup-avatar-wrap">
            <img src={avatarSrc} alt="avatar" className="mup-avatar" />
            <span className="mup-username">{userData?.username || ''}</span>
            {userData?.face_login_registered && (
              <Tag
                icon={<CheckCircleOutlined />}
                color="success"
                style={{ marginTop: 4, fontSize: 11 }}>
                Đã đăng ký khuôn mặt
              </Tag>
            )}
          </div>
          <div className="mup-bio-wrap">
            <label className="mup-label">Thông tin giới thiệu bản thân</label>
            <Input.TextArea
              value={formValues.description}
              rows={4}
              placeholder="Giới thiệu về bản thân..."
              className="mup-textarea"
              disabled
            />
          </div>
        </div>

        {/* Họ tên + Giới tính */}
        <div className="mup-row">
          <BKInput
            label="Họ và tên"
            name="fullname"
            value={formValues.fullname}
            placeholder="Nhập họ và tên"
            disabled
          />
          <BKSelect
            label="Giới tính"
            name="gender"
            value={formValues.gender}
            options={GENDER_OPTIONS}
            placeholder="Chọn giới tính"
            showSearch={false}
            disabled
          />
        </div>

        {/* CCCD + Ngày cấp */}
        <div className="mup-row">
          <BKInput
            label="Số CCCD"
            name="id_num"
            value={formValues.id_num}
            placeholder="Nhập số CCCD"
            disabled
          />
          <div className="mup-field">
            <label className="mup-label">Ngày cấp</label>
            <DatePicker
              value={
                formValues.date_inssue_id_num
                  ? moment(formValues.date_inssue_id_num)
                  : null
              }
              format="DD/MM/YYYY"
              locale={locale}
              placeholder="Chọn ngày cấp"
              style={{ width: '100%' }}
              disabled
            />
          </div>
        </div>

        {/* Tỉnh thành + Quận/Huyện (required) */}
        <div className="mup-row">
          <BKSelect
            label="Tỉnh/Thành phố"
            name="province_id"
            value={formValues.province_id || undefined}
            options={provinces}
            getOptionLabel={(o) => o.title}
            getOptionValue={(o) => o.id}
            placeholder="Chọn tỉnh/thành phố"
            disabled
          />
          <BKSelect
            label="Quận/Huyện"
            name="district_id"
            value={formValues.district_id || undefined}
            options={districts}
            getOptionLabel={(o) => o.title}
            getOptionValue={(o) => o.id}
            placeholder="Chọn quận/huyện"
            disabled
          />
        </div>

        {/* Email + Điện thoại */}
        <div className="mup-row">
          <BKInput
            label="Email"
            name="email"
            value={formValues.email}
            placeholder="Nhập email"
            disabled
          />
          <BKInput
            label="Số điện thoại"
            name="cell_phone"
            value={formValues.cell_phone}
            placeholder="Nhập số điện thoại"
            disabled
          />
        </div>

        {/* Địa chỉ */}
        <div className="mup-row">
          <BKInput
            label="Địa chỉ"
            name="address"
            value={formValues.address}
            placeholder="Nhập địa chỉ"
            disabled
          />
        </div>

        {/* Facebook + Instagram + Tiktok */}
        <div className="mup-row mup-row-3">
          <BKInput
            label="Facebook"
            name="facebook"
            value={formValues.facebook}
            placeholder="Facebook"
            disabled
          />
          <BKInput
            label="Instagram"
            name="instagram"
            value={formValues.instagram}
            placeholder="Instagram"
            disabled
          />
          <BKInput
            label="Tiktok"
            name="tiktok"
            value={formValues.tiktok}
            placeholder="Tiktok"
            disabled
          />
        </div>
      </div>
    </BKModal>
    <LoginHistoryModal
      open={openLoginHistory}
      onClose={() => setOpenLoginHistory(false)}
    />
    <SuccessModal
      open={openFaceSuccess}
      onClose={() => setOpenFaceSuccess(false)}
    />
    <FaceLoginModal
      open={openFaceRegister}
      onClose={() => {
        setOpenFaceRegister(false);
        setFaceError('');
        setFaceLoading(false);
      }}
      onCapture={handleFaceCapture}
      loading={faceLoading}
      errorMessage={faceError}
      onClearError={() => setFaceError('')}
      title="Đăng ký khuôn mặt"
      confirmText="Đăng ký"
      loadingText="Đang đăng ký..."
    />
    </>
  );
};

export default ModalUserProfile;
